import {
  ActionButton,
  Body,
  Icon,
  LoadingPlaceholder,
  Modal,
  Tooltip,
} from "@indigo-components";
import { Button, Input } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { zIndex } from "~/indigo-components/src/styles/tokens";
import type { UseAgentChatReturn } from "~/pages/appointments/hooks/useAgentChat";
import { PANEL_SIDEBAR_WIDTH } from "./chatSurfaceShared";

const RAIL_SIDEBAR_WIDTH = 280;
/** Off-canvas drawer width on phones — capped by viewport so it never covers
 *  the whole screen (the peek of chat behind it is the affordance to dismiss). */
const DRAWER_SIDEBAR_WIDTH = "min(300px, 82vw)";

interface ConversationSidebarProps {
  /**
   * "foldable" (panel) animates 0↔width; "rail" (fullscreen) is permanent;
   * "drawer" (fullscreen on phones) slides in over the chat.
   */
  variant: "foldable" | "rail" | "drawer";
  /** Foldable/drawer — whether the rail is open. Ignored for "rail". */
  open: boolean;
  chat: UseAgentChatReturn;
  /**
   * Drawer only: dismiss the drawer. Fired by the close button and after a
   * conversation is picked — on a phone the drawer covers the chat, so leaving
   * it open would hide the conversation the tap just opened.
   */
  onDismiss?: () => void;
}

export const ConversationSidebar: FC<ConversationSidebarProps> = ({
  variant,
  open,
  chat,
  onDismiss,
}) => {
  const {
    conversations,
    conversationId,
    isStreaming,
    isLoadingConversations,
    hasMoreConversations,
    loadMoreConversations,
    isLoadingHistory,
    messages,
    selectConversation,
    renameConversation,
    archiveConversation,
  } = chat;

  const rail = variant === "rail";
  const drawer = variant === "drawer";
  const innerWidth = drawer
    ? DRAWER_SIDEBAR_WIDTH
    : rail
      ? RAIL_SIDEBAR_WIDTH
      : PANEL_SIDEBAR_WIDTH;
  const enabled = rail || open;

  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [renamingConvId, setRenamingConvId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
  const renameCancelledRef = useRef(false);
  // Captured once at mount so the unsaved-conversation placeholder date doesn't
  // recompute on every render (and won't drift if left open across midnight).
  const placeholderDate = useMemo(() => new Date().toLocaleDateString(), []);

  // ── Sidebar scroll loading via IntersectionObserver ──
  useEffect(() => {
    if (!enabled || !hasMoreConversations || isLoadingConversations) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreConversations &&
          !isLoadingConversations
        ) {
          void loadMoreConversations();
        }
      },
      {
        root: containerRef.current,
        rootMargin: "100px 0px 0px 0px",
        threshold: 0,
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    enabled,
    hasMoreConversations,
    isLoadingConversations,
    loadMoreConversations,
  ]);

  const handleSelectConversation = useCallback(
    (convId: string) => {
      void selectConversation(convId);
      onDismiss?.();
    },
    [selectConversation, onDismiss],
  );

  const handleRenameStart = useCallback(
    (convId: string, currentTitle: string) => {
      renameCancelledRef.current = false;
      setRenamingConvId(convId);
      setRenameValue(currentTitle);
    },
    [],
  );

  const handleRenameCancel = useCallback(() => {
    renameCancelledRef.current = true;
    setRenamingConvId(null);
    setRenameValue("");
  }, []);

  const handleRenameConfirm = useCallback(() => {
    if (renameCancelledRef.current) {
      renameCancelledRef.current = false;
      return;
    }
    if (renamingConvId && renameValue.trim()) {
      renameConversation(renamingConvId, renameValue.trim());
    }
    setRenamingConvId(null);
    setRenameValue("");
  }, [renamingConvId, renameValue, renameConversation]);

  const handleArchiveConfirm = useCallback(() => {
    if (archiveConfirmId) {
      archiveConversation(archiveConfirmId);
      setArchiveConfirmId(null);
    }
  }, [archiveConfirmId, archiveConversation]);

  return (
    <div
      className={`chatbot-sidebar${drawer ? " chatbot-sidebar--drawer" : ""}`}
      // Drawer: pinned to the viewport and slid in/out by transform, so opening
      // it never reflows the chat underneath (which on a phone is the whole
      // screen). Rail/foldable keep the in-flow width animation.
      style={
        drawer
          ? {
              position: "fixed",
              top: 0,
              // Height rather than `bottom: 0`: a fixed box resolves against the
              // layout viewport (the large one on iOS), which would put the last
              // ~40px of the list under the URL bar.
              height: "100dvh",
              left: 0,
              width: innerWidth,
              display: "flex",
              flexDirection: "column",
              transform: open ? "translateX(0)" : "translateX(-100%)",
              // Off-screen alone still leaves the rail in the tab order.
              visibility: open ? "visible" : "hidden",
              // Below Ant's popup tier so this sidebar's own archive Modal —
              // and any dropdown opened over the chat — still lands on top.
              zIndex: zIndex.layout,
              borderRight: "1px solid var(--indigo-border-default)",
              backgroundColor: "var(--indigo-canvas-shade)",
              boxShadow: "0 9px 28px 8px rgba(0, 0, 0, 0.05)",
            }
          : {
              width: enabled ? innerWidth : 0,
              flexShrink: 0,
              overflow: "hidden",
              // A zero-width foldable rail still paints its border. When
              // closed, that line lands beside the panel's outer border and
              // makes the left edge look twice as thick as the right edge.
              borderRightWidth: enabled ? 1 : 0,
              borderRightStyle: "solid",
              borderRightColor: "var(--indigo-border-default)",
              backgroundColor: rail ? "var(--indigo-canvas-shade)" : undefined,
            }
      }
      aria-hidden={drawer && !open}
    >
      {drawer && (
        <div
          className="flex items-center justify-between px-3 py-2"
          style={{
            borderBottom: "1px solid var(--indigo-border-default)",
            flexShrink: 0,
          }}
        >
          <Body
            as="span"
            size="sm"
            style={{ fontWeight: 500, color: "var(--indigo-text-secondary)" }}
          >
            Conversations
          </Body>
          <button
            type="button"
            className="chatbot-touch-btn"
            onClick={onDismiss}
            aria-label="Close conversations"
          >
            <Icon name="functional-close" sizeX={14} />
          </button>
        </div>
      )}

      {/* Inner wrapper: fixed width prevents reflow during animation; handles
          its own scroll. */}
      <div
        ref={containerRef}
        style={{
          width: drawer ? "100%" : innerWidth,
          minHeight: drawer ? 0 : "100%",
          maxHeight: drawer ? undefined : "100%",
          flex: drawer ? 1 : undefined,
          overflowY: "auto",
          // Momentum scrolling + no scroll chaining into the chat behind.
          overscrollBehavior: drawer ? "contain" : undefined,
        }}
      >
        {conversations.length === 0 &&
          !isLoadingConversations &&
          messages.length > 0 && (
            <div
              className="flex items-center justify-center h-full text-xs"
              style={{ color: "var(--indigo-text-secondary)" }}
            >
              No conversations yet
            </div>
          )}

        {/* Placeholder for unsaved new conversation */}
        {messages.length === 0 &&
          !isLoadingHistory &&
          !conversations.some((c) => c.conversationId === conversationId) && (
            <div
              style={{
                padding: "10px 12px",
                backgroundColor: "var(--indigo-gray-100)",
                borderBottom: "1px solid var(--indigo-border-default)",
              }}
            >
              <div
                className="text-sm leading-snug"
                style={{ color: "var(--indigo-text-primary)" }}
              >
                New conversation
              </div>
              <div
                className="text-[10px] mt-1"
                style={{ color: "var(--indigo-text-secondary)" }}
              >
                {placeholderDate}
              </div>
            </div>
          )}

        {conversations.map((conv) => (
          <div
            key={conv.conversationId}
            onClick={() =>
              !isStreaming &&
              renamingConvId !== conv.conversationId &&
              handleSelectConversation(conv.conversationId)
            }
            className={`group relative transition-colors ${isStreaming && conv.conversationId !== conversationId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            style={{
              padding: "10px 12px",
              backgroundColor:
                conv.conversationId === conversationId
                  ? "var(--indigo-gray-100)"
                  : "transparent",
              borderBottom: "1px solid var(--indigo-border-default)",
            }}
            onMouseEnter={(e) => {
              if (conv.conversationId !== conversationId && !isStreaming) {
                e.currentTarget.style.backgroundColor =
                  "var(--indigo-gray-50, #f9fafb)";
              }
            }}
            onMouseLeave={(e) => {
              if (conv.conversationId !== conversationId && !isStreaming) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            {renamingConvId === conv.conversationId ? (
              <Input
                size="small"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onPressEnter={handleRenameConfirm}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    handleRenameCancel();
                  }
                }}
                onBlur={handleRenameConfirm}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                style={{
                  // 16px on the drawer for the same reason as the composer:
                  // under 16px iOS Safari zooms the page on focus and never
                  // zooms back out.
                  fontSize: drawer ? 16 : 13,
                  padding: "2px 6px",
                  borderColor: "var(--indigo-border-default)",
                }}
              />
            ) : (
              <div
                // Clearance for the trailing rename/archive buttons — wider in
                // the drawer, where they grow to a 32px touch target.
                className={`text-sm leading-snug ${drawer ? "pr-20" : "pr-10"}`}
                style={{
                  color: "var(--indigo-text-primary)",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {conv.title}
              </div>
            )}
            <div
              className="text-[10px] mt-1"
              style={{ color: "var(--indigo-text-secondary)" }}
            >
              {conv.lastMessageAt.toLocaleDateString()}
            </div>
            {/* Hover actions — rename & archive */}
            {renamingConvId !== conv.conversationId && (
              <div
                className={`absolute right-1 top-1.5 transition-opacity flex gap-0.5 ${
                  // Touch has no hover — on the drawer these would be
                  // unreachable, so they stay visible.
                  drawer ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                }`}
              >
                <Tooltip content="Rename" placement="top" trigger="mouseenter">
                  <Button
                    type="text"
                    size="small"
                    icon={<Icon name="pencil" size="2xs" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenameStart(conv.conversationId, conv.title);
                    }}
                    style={{
                      color: "var(--indigo-text-secondary)",
                      width: 22,
                      height: 22,
                      minWidth: 22,
                    }}
                  />
                </Tooltip>
                <Tooltip content="Archive" placement="top" trigger="mouseenter">
                  <Button
                    type="text"
                    size="small"
                    icon={<Icon name="trash" size="2xs" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setArchiveConfirmId(conv.conversationId);
                    }}
                    style={{
                      color: "var(--indigo-danger, #ef4444)",
                      width: 22,
                      height: 22,
                      minWidth: 22,
                    }}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        ))}

        {/* Sidebar load-more sentinel */}
        {hasMoreConversations && (
          <div
            ref={sentinelRef}
            className="flex items-center justify-center py-3"
          >
            {isLoadingConversations && <LoadingPlaceholder size="sm" />}
          </div>
        )}
      </div>

      {/* Archive confirmation modal */}
      <Modal
        title="Archive this conversation?"
        open={archiveConfirmId !== null}
        onCancel={() => setArchiveConfirmId(null)}
        footer={null}
        centered
        width={360}
      >
        <Body
          size="md"
          style={{ color: "var(--indigo-text-secondary)" }}
          className="mb-4"
        >
          This conversation will be moved to archives and hidden from the
          sidebar.
        </Body>
        <div className="flex justify-end gap-2">
          <ActionButton
            variant="ghost"
            size="md"
            text="Cancel"
            onClick={() => setArchiveConfirmId(null)}
          />
          <ActionButton
            variant="danger"
            size="md"
            text="Archive"
            onClick={handleArchiveConfirm}
          />
        </div>
      </Modal>
    </div>
  );
};
